import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { UncontrolledTooltip } from 'reactstrap';
import { AdminAgentInfoDto, AgentDealStatus, DealType } from '@repo/common-types';
import AgentQuickActions from './components/AgentQuickActions';
import { formatCurrency } from '@/common/utils/formatCurrency';
import { ColumnAlignment } from '@/common/tableData/DataTable';
import { Link } from 'react-router-dom';
import ROUTES from '@/routes/routes-enums';
import { formatDealPercentage } from '@repo/utils';

const getAgreementType = (row: AdminAgentInfoDto): string => {
  if (row.currentUserAgreement?.type && row.currentUserAgreement?.type !== 'None') {
    return row.currentUserAgreement.type;
  }
  if (row.agentDeal) {
    return 'agent_deal_v3';
  }
  return '-';
};

/**
 * Creates column definitions for the Agents table
 * Displays user information, agreement details, and agent deal information
 */
export const createColumns = (
  canViewProfile: boolean,
  handleCreateDeal: (userId: string) => void,
  handleEditDeal: (dealId: string) => void,
  handleDeactivateDeal: (dealId: string) => void,
): ColumnDef<AdminAgentInfoDto>[] => [
  // Actions Column
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const agent = row.original;

      return (
        <AgentQuickActions
          agent={agent}
          onCreateDeal={handleCreateDeal}
          onEditDeal={handleEditDeal}
          onDeactivateDeal={handleDeactivateDeal}
          canViewProfile={canViewProfile}
        />
      );
    },
    enableSorting: false,
    size: 80,
    meta: {
      alignment: ColumnAlignment.CENTER,
    },
  },

  // User Information Columns
  {
    accessorKey: 'lastName',
    header: 'Name',
    cell: ({ row }) => {
      const fullName =
        row.original.lastName && row.original.firstName ? `${row.original.lastName}, ${row.original.firstName}` : '';
      const email = row.original.email;
      const tooltipId = `agent-name-${row.original.id}`;

      return (
        <>
          <div className="d-flex flex-column" id={tooltipId}>
            <span className="text-truncate" style={{ maxWidth: '180px' }}>
              {fullName}
            </span>

            <small className="text-muted text-truncate" style={{ maxWidth: '180px' }}>
              {email}
            </small>
          </div>
          <UncontrolledTooltip placement="top" target={tooltipId}>
            <div>
              <div>{fullName}</div>
              <div>{email}</div>
            </div>
          </UncontrolledTooltip>
        </>
      );
    },
    enableSorting: true,
    size: 200,
  },
  {
    accessorKey: 'alias',
    header: 'Alias',
    cell: ({ row }) => {
      const alias = row.original.alias;
      const tooltipId = `agent-alias-${row.original.id}`;

      return (
        <>
          <Link to={`${ROUTES.USER_EDIT}/${row.original.id}`} className="text-decoration-underline cursor-pointer">
            <span className="text-truncate" style={{ maxWidth: '130px' }} id={tooltipId}>
              {alias}
            </span>
          </Link>
          {alias && (
            <UncontrolledTooltip placement="top" target={tooltipId}>
              {alias}
            </UncontrolledTooltip>
          )}
        </>
      );
    },
    enableSorting: true,
    size: 150,
  },
  // Agent Deal Status with Priority Indicator
  {
    id: 'dealStatus',
    header: 'Status',
    accessorKey: 'dealStatus',
    cell: ({ getValue, row }) => {
      const hasAgentDeal = row.original.hasAgentDeal;
      const hasUserAgreement = row.original.currentUserAgreement && row.original.currentUserAgreement?.type !== 'None';
      const isUserAgreementActive = row.original.currentUserAgreement?.isActive
        ? AgentDealStatus.ACTIVE
        : AgentDealStatus.INACTIVE;

      const status = hasUserAgreement && !hasAgentDeal ? isUserAgreementActive : (getValue() as AgentDealStatus);

      const className =
        status === AgentDealStatus.ACTIVE
          ? 'text-success'
          : status === AgentDealStatus.UPCOMING
            ? 'text-warning'
            : status === AgentDealStatus.INACTIVE
              ? 'text-muted'
              : 'text-muted';

      return (
        <div className="d-flex flex-column gap-1">
          <span className={className}>{status}</span>
        </div>
      );
    },
    enableSorting: true,
    size: 130,
  },
  // Current UserAgreement Type with Priority Indicator
  {
    id: 'userAgreementType',
    header: 'Agreement Type',
    accessorFn: (row) => getAgreementType(row),
    cell: ({ getValue, row }) => {
      const agreementType = (getValue() as string) ?? getAgreementType(row.original);
      const className = agreementType === '-' ? 'text-muted' : '';
      return (
        <div>
          <span className={className}>{agreementType}</span>
        </div>
      );
    },
    enableSorting: true,
    size: 180,
  },

  // Deal Type (if AgentDeal exists)
  {
    id: 'dealType',
    header: 'Deal Type',
    accessorFn: (row) => row.agentDeal?.dealType || 'None',
    cell: ({ getValue, row }) => {
      // if user has agent deal we use the agent deal value
      // else we check if the have a referral_v2 agreement and default to OVERRIDE
      // else empty
      const hasAgentDeal = row.original.hasAgentDeal;
      const hasActiveAgreement = row.original.currentUserAgreement?.isActive;
      const agreementType = row.original.currentUserAgreement?.type;

      if (!hasAgentDeal && !hasActiveAgreement) {
        return <span className="text-muted">-</span>;
      }
      const dealType = hasAgentDeal ? (getValue() as DealType) : DealType.OVERRIDE;

      const label = dealType === DealType.OVERRIDE ? 'Override' : 'Flat';

      const rakeSharePercentage = hasAgentDeal
        ? row.original?.agentDeal?.rakeSharePercentage
        : hasActiveAgreement && agreementType === 'referral_v2'
          ? row.original?.currentUserAgreement?.rakeSharePercentage
          : undefined;

      const value =
        dealType === DealType.OVERRIDE
          ? rakeSharePercentage
            ? formatDealPercentage(rakeSharePercentage)
            : '-'
          : hasAgentDeal
            ? `$${formatCurrency(row.original?.agentDeal?.flatFee ?? 0, 0)}`
            : '-';

      return (
        <div className="d-flex flex-column">
          <span>{label}</span>
          <small className="text-muted">{value}</small>
        </div>
      );
    },
    enableSorting: true,
    size: 100,
  },
  {
    id: 'isRegionalManager',
    header: 'Regional Manager',
    accessorFn: (row) => row.agentDeal?.isRegionalManager || false,
    cell: ({ getValue, row }) => {
      const hasAgentDeal = row.original.hasAgentDeal;
      if (!hasAgentDeal) {
        return <span className="text-muted">-</span>;
      }

      const isRegionalManager = getValue() as boolean;
      if (isRegionalManager) {
        return <span>Yes</span>;
      }
      return <span className="text-muted">No</span>;
    },
    enableSorting: true,
    size: 100,
  },

  // Deal Validity Period
  {
    id: 'createdAt',
    header: 'Deal Assigned',

    cell: ({ row }) => {
      const { agentDeal, currentUserAgreement } = row.original;

      // Determine if we have a valid deal/agreement to show
      const hasValidAgreement = currentUserAgreement && currentUserAgreement.type !== 'None';
      const hasValidDeal = Boolean(agentDeal);

      // If neither exists, show placeholder
      if (!hasValidDeal && !hasValidAgreement) {
        return <span className="text-muted">-</span>;
      }

      // Get the appropriate date - prioritize agentDeal over userAgreement
      const date = agentDeal ? new Date(agentDeal.startDate) : new Date(currentUserAgreement!.createdAt);

      return (
        <div className="d-flex flex-column">
          <span className="fw-medium">{format(date, 'yyyy-MM-dd')}</span>
          <small className="text-muted">{format(date, 'HH:mm')}</small>
        </div>
      );
    },
    enableSorting: true,
    size: 150,
  },
];
